'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Widget {
  id: string;
  name: string;
  icon: string;
  category: string;
  description: string;
}

interface WidgetLibraryProps {
  onWidgetDragStart: (widget: Widget) => void;
}

const WidgetLibrary = ({ onWidgetDragStart }: WidgetLibraryProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const widgets: Widget[] = [
    {
      id: 'stat-card',
      name: 'Cartão de Estatística',
      icon: 'ChartBarIcon',
      category: 'statistics',
      description: 'Exibe métricas numéricas com ícone',
    },
    {
      id: 'line-chart',
      name: 'Gráfico de Linha',
      icon: 'ChartBarIcon',
      category: 'charts',
      description: 'Visualiza tendências ao longo do tempo',
    },
    {
      id: 'bar-chart',
      name: 'Gráfico de Barras',
      icon: 'ChartBarSquareIcon',
      category: 'charts',
      description: 'Compara valores entre categorias',
    },
    {
      id: 'pie-chart',
      name: 'Gráfico de Pizza',
      icon: 'ChartPieIcon',
      category: 'charts',
      description: 'Mostra distribuição percentual',
    },
    {
      id: 'task-list',
      name: 'Lista de Tarefas',
      icon: 'ClipboardDocumentCheckIcon',
      category: 'tasks',
      description: 'Exibe tarefas pendentes do usuário',
    },
    {
      id: 'process-list',
      name: 'Lista de Processos',
      icon: 'RectangleStackIcon',
      category: 'processes',
      description: 'Mostra processos ativos e recentes',
    },
    {
      id: 'form-widget',
      name: 'Formulário Rápido',
      icon: 'DocumentTextIcon',
      category: 'forms',
      description: 'Formulário incorporado para entrada rápida',
    },
    {
      id: 'content-block',
      name: 'Bloco de Conteúdo',
      icon: 'DocumentIcon',
      category: 'content',
      description: 'Texto rico e conteúdo HTML',
    },
    {
      id: 'analytics-widget',
      name: 'Widget de Análise',
      icon: 'PresentationChartLineIcon',
      category: 'analytics',
      description: 'KPIs e métricas de desempenho',
    },
    {
      id: 'custom-html',
      name: 'HTML Personalizado',
      icon: 'CodeBracketIcon',
      category: 'custom',
      description: 'Código HTML/JS personalizado',
    },
    {
      id: 'calendar-widget',
      name: 'Calendário',
      icon: 'CalendarIcon',
      category: 'content',
      description: 'Visualização de calendário de eventos',
    },
    {
      id: 'notification-feed',
      name: 'Feed de Notificações',
      icon: 'BellIcon',
      category: 'content',
      description: 'Últimas notificações e alertas',
    },
  ];

  const categories = [
    { id: 'all', name: 'Todos', icon: 'Squares2X2Icon' },
    { id: 'statistics', name: 'Estatísticas', icon: 'ChartBarIcon' },
    { id: 'charts', name: 'Gráficos', icon: 'ChartPieIcon' },
    { id: 'tasks', name: 'Tarefas', icon: 'ClipboardDocumentCheckIcon' },
    { id: 'processes', name: 'Processos', icon: 'RectangleStackIcon' },
    { id: 'forms', name: 'Formulários', icon: 'DocumentTextIcon' },
    { id: 'content', name: 'Conteúdo', icon: 'DocumentIcon' },
    { id: 'analytics', name: 'Análise', icon: 'PresentationChartLineIcon' },
    { id: 'custom', name: 'Personalizado', icon: 'CodeBracketIcon' },
  ];

  const filteredWidgets = widgets.filter((widget) => {
    const matchesSearch = widget.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      widget.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || widget.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDragStart = (e: React.DragEvent, widget: Widget) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify(widget));
    onWidgetDragStart(widget);
  };

  return (
    <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:135:4" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="135" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20flex%20flex-col%20bg-surface%20border-r%20border-border%22%7D" className="h-full flex flex-col bg-surface border-r border-border">
      <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:136:6" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="136" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20border-b%20border-border%22%7D" className="p-4 border-b border-border">
        <h2 data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:137:8" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="137" data-component-file="WidgetLibrary.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Biblioteca%20de%20Widgets%22%7D" className="text-lg font-semibold text-foreground mb-4">Biblioteca de Widgets</h2>
        
        <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:139:8" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="139" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mb-4%22%7D" className="relative mb-4">
          <Icon data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:140:10" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="140" data-component-file="WidgetLibrary.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
            name="MagnifyingGlassIcon"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:145:10" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="145" data-component-file="WidgetLibrary.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-10%20pr-4%20py-2%20bg-muted%20border%20border-border%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar widgets..."
            className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:154:8" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="154" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-1%22%7D" className="space-y-1">
          {categories.map((category) => (
            <button data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:156:12" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="156" data-component-file="WidgetLibrary.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-micro ${
                selectedCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:165:14" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="165" data-component-file="WidgetLibrary.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                name={category.icon as any}
                size={18}
                className={selectedCategory === category.id ? 'text-primary-foreground' : 'text-muted-foreground'}
              />
              <span data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:170:14" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="170" data-component-file="WidgetLibrary.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%22%7D" className="font-medium">{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:176:6" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="176" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20p-4%22%7D" className="flex-1 overflow-y-auto p-4">
        <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:177:8" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="177" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
          {filteredWidgets.map((widget) => (
            <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:179:12" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="179" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20bg-card%20border%20border-border%20rounded-md%20cursor-move%20hover%3Aborder-accent%20hover%3Ashadow-subtle%20transition-micro%22%7D"
              key={widget.id}
              draggable
              onDragStart={(e) => handleDragStart(e, widget)}
              className="p-3 bg-card border border-border rounded-md cursor-move hover:border-accent hover:shadow-subtle transition-micro"
            >
              <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:185:14" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="185" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
                <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:186:16" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="186" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-10%20h-10%20bg-accent%2F10%20rounded-md%20flex%20items-center%20justify-center%22%7D" className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-md flex items-center justify-center">
                  <Icon data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:187:18" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="187" data-component-file="WidgetLibrary.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-accent%22%7D" name={widget.icon as any} size={20} className="text-accent" />
                </div>
                <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:189:16" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="189" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                  <h3 data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:190:18" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="190" data-component-file="WidgetLibrary.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-medium%20text-foreground%20mb-1%22%7D" className="text-sm font-medium text-foreground mb-1">{widget.name}</h3>
                  <p data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:191:18" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="191" data-component-file="WidgetLibrary.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20line-clamp-2%22%7D" className="text-xs text-muted-foreground line-clamp-2">{widget.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredWidgets.length === 0 && (
          <div data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:199:10" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="199" data-component-file="WidgetLibrary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%22%7D" className="text-center py-8">
            <Icon data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:200:12" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="200" data-component-file="WidgetLibrary.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22mx-auto%20mb-2%20text-muted-foreground%20opacity-50%22%7D" name="MagnifyingGlassIcon" size={48} className="mx-auto mb-2 text-muted-foreground opacity-50" />
            <p data-component-id="src/app/portal-builder/components/WidgetLibrary.tsx:201:12" data-component-path="src/app/portal-builder/components/WidgetLibrary.tsx" data-component-line="201" data-component-file="WidgetLibrary.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Nenhum%20widget%20encontrado%22%7D" className="text-sm text-muted-foreground">Nenhum widget encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetLibrary;